import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField, Alert, Divider,
  Chip, CircularProgress, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, Paper, Grid, InputAdornment, IconButton, Stepper,
  Step, StepLabel,
} from '@mui/material';
import {
  Security, CheckCircle, Lock, LockOpen, QrCode2, Key, VpnKey,
  Warning, Visibility, VisibilityOff, Devices,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';
import { useSnackbar } from 'notistack';

export function SecurityPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [mfaStatus, setMfaStatus] = useState<{ mfa_enabled: boolean; mfa_required: boolean; role: string } | null>(null);
  const [devices, setDevices] = useState<any[]>([]);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Setup flow state
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupStep, setSetupStep] = useState(0);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState('');

  // Disable flow state
  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);
  const [disableError, setDisableError] = useState('');

  // Change password
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try {
      const { data } = await api.get('/auth/mfa/status');
      setMfaStatus(data);
    } catch (e) { console.error(e); }
    finally { setLoadingStatus(false); }
  };

  const loadDevices = () => api.get('/auth/devices').then(({ data }) => setDevices(data || [])).catch(() => setDevices([]));
  const revokeDevice = async (id: string) => { await api.delete(`/auth/devices/${id}`); loadDevices(); enqueueSnackbar('Device revoked', { variant: 'info' }); };
  const revokeAllDevices = async () => {
    await api.delete('/auth/devices');
    localStorage.removeItem('device_token');
    try {   // forget this account's per-login trusted token (other accounts on this browser keep theirs)
      const me = useAuthStore.getState().user as any;
      const m = JSON.parse(localStorage.getItem('device_tokens') || '{}');
      [me?.email, me?.username].filter(Boolean).forEach((k: string) => delete m[k.toLowerCase()]);
      localStorage.setItem('device_tokens', JSON.stringify(m));
    } catch { /* ignore */ }
    loadDevices(); enqueueSnackbar('All trusted devices revoked — 2FA required at next login', { variant: 'info' }); };
  useEffect(() => { loadStatus(); loadDevices(); }, []);

  const openSetup = async () => {
    setSetupStep(0);
    setSetupCode('');
    setSetupError('');
    setSetupLoading(true);
    setSetupOpen(true);
    try {
      const { data } = await api.post('/auth/mfa/setup');
      setQrCode(data.qr_code);
      setSecret(data.secret);
      setSetupStep(1);
    } catch (e: any) {
      setSetupError(e.response?.data?.message || 'Failed to generate QR code');
    } finally { setSetupLoading(false); }
  };

  const confirmSetup = async () => {
    if (setupCode.length !== 6) { setSetupError('Enter the 6-digit code'); return; }
    setSetupLoading(true);
    setSetupError('');
    try {
      await api.post('/auth/mfa/enable', { code: setupCode });
      setSetupStep(2);
      enqueueSnackbar('2FA enabled successfully!', { variant: 'success' });
      await loadStatus();
    } catch (e: any) {
      setSetupError(e.response?.data?.message || 'Invalid code — try again');
    } finally { setSetupLoading(false); }
  };

  const confirmDisable = async () => {
    if (disableCode.length !== 6) { setDisableError('Enter the 6-digit code'); return; }
    setDisableLoading(true);
    setDisableError('');
    try {
      await api.post('/auth/mfa/disable', { code: disableCode });
      setDisableOpen(false);
      setDisableCode('');
      enqueueSnackbar('2FA disabled', { variant: 'info' });
      await loadStatus();
    } catch (e: any) {
      setDisableError(e.response?.data?.message || 'Invalid code');
    } finally { setDisableLoading(false); }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess(false);
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('Passwords do not match'); return;
    }
    if (pwForm.new_password.length < 8) {
      setPwError('Password must be at least 8 characters'); return;
    }
    setPwLoading(true);
    try {
      await api.patch('/auth/change-password', {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      setPwSuccess(true);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (e: any) {
      setPwError(e.response?.data?.message || 'Password change failed');
    } finally { setPwLoading(false); }
  };

  if (loadingStatus) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 780 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <Security color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Account Security</Typography>
          <Typography variant="body2" color="text.secondary">Manage 2FA and password settings</Typography>
        </Box>
      </Stack>

      {!mfaStatus?.mfa_enabled && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<Security />}>
          <strong>Two-Factor Authentication is optional.</strong> Enable it below for extra account security.
          Once your deployment is finalised, you can make it mandatory system-wide.
        </Alert>
      )}

      {/* ── 2FA Section ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {mfaStatus?.mfa_enabled
                ? <CheckCircle color="success" />
                : <LockOpen color="warning" />}
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Two-Factor Authentication
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  TOTP via Google Authenticator or Authy
                </Typography>
              </Box>
            </Box>
            <Chip
              label={mfaStatus?.mfa_enabled ? 'Enabled' : 'Disabled'}
              color={mfaStatus?.mfa_enabled ? 'success' : 'default'}
              icon={mfaStatus?.mfa_enabled ? <CheckCircle /> : <LockOpen />}
            />
          </Stack>



          {mfaStatus?.mfa_enabled ? (
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" color="error" startIcon={<LockOpen />}
                onClick={() => { setDisableCode(''); setDisableError(''); setDisableOpen(true); }}>
                Disable 2FA
              </Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={2}>
              <Button variant="contained" color="success" startIcon={<QrCode2 />} onClick={openSetup}>
                Set Up 2FA
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ── Trusted Devices Section ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Devices color="primary" />
              <Box>
                <Typography variant="h6" fontWeight={600}>Trusted Devices</Typography>
                <Typography variant="body2" color="text.secondary">Devices that skip the 2FA code at login (30-day trust). Password is still required.</Typography>
              </Box>
            </Stack>
            {devices.length > 0 && (
              <Button size="small" color="error" variant="outlined" onClick={revokeAllDevices}>Revoke all</Button>
            )}
          </Stack>
          {devices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No trusted devices. Tick "Trust this device" at the 2FA step to add this computer.</Typography>
          ) : (
            <Stack spacing={1}>
              {devices.map((d: any) => (
                <Paper key={d.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Devices fontSize="small" color="action" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{d.label || 'Device'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Added {new Date(d.created_at).toLocaleDateString()} · last used {d.last_used_at ? new Date(d.last_used_at).toLocaleString() : '—'} · expires {new Date(d.expires_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Button size="small" color="error" onClick={() => revokeDevice(d.id)}>Revoke</Button>
                </Paper>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ── Change Password Section ── */}
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <Key color="primary" />
            <Typography variant="h6" fontWeight={600}>Change Password</Typography>
          </Stack>

          {pwSuccess && <Alert severity="success" sx={{ mb: 2 }}>Password changed successfully.</Alert>}
          {pwError && <Alert severity="error" sx={{ mb: 2 }}>{pwError}</Alert>}

          <form onSubmit={handlePasswordChange}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Current Password"
                  type={showPw ? 'text' : 'password'} value={pwForm.current_password}
                  onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })}
                  InputProps={{ endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPw(v => !v)}>
                        {showPw ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )}} />
              </Grid>
              <Grid item xs={12} md={6} />
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="New Password"
                  type={showPw ? 'text' : 'password'} value={pwForm.new_password}
                  onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })}
                  helperText="Minimum 8 characters" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Confirm New Password"
                  type={showPw ? 'text' : 'password'} value={pwForm.confirm_password}
                  onChange={e => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                  error={!!pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password}
                  helperText={pwForm.confirm_password && pwForm.new_password !== pwForm.confirm_password ? 'Does not match' : ''} />
              </Grid>
              <Grid item xs={12}>
                <Button type="submit" variant="contained" startIcon={<VpnKey />}
                  disabled={pwLoading || !pwForm.current_password || !pwForm.new_password || !pwForm.confirm_password}>
                  {pwLoading ? <CircularProgress size={20} /> : 'Change Password'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* ── Setup 2FA Dialog ── */}
      <Dialog open={setupOpen} onClose={() => setupStep !== 0 && setSetupOpen(false)} maxWidth="sm" fullWidth
        disableEscapeKeyDown={setupStep === 0}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCode2 color="success" /> Set Up Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={setupStep > 0 ? setupStep - 1 : 0} sx={{ mb: 3 }}>
            {['Scan QR Code', 'Confirm Code', 'Done'].map(l => (
              <Step key={l}><StepLabel>{l}</StepLabel></Step>
            ))}
          </Stepper>

          {setupStep === 0 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }} color="text.secondary">Generating QR code…</Typography>
            </Box>
          )}

          {setupStep === 1 && (
            <Box>
              {setupError && <Alert severity="error" sx={{ mb: 2 }}>{setupError}</Alert>}
              <Typography gutterBottom>
                1. Open <strong>Google Authenticator</strong> or <strong>Authy</strong> on your phone.
              </Typography>
              <Typography gutterBottom>
                2. Tap <strong>+</strong> → <em>Scan a QR code</em> and scan the image below.
              </Typography>
              {qrCode && (
                <Box sx={{ textAlign: 'center', my: 2 }}>
                  <img src={qrCode} alt="QR Code" style={{ width: 200, height: 200, border: '4px solid #eee', borderRadius: 8 }} />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Can't scan? Enter this key manually in your app:
              </Typography>
              <Paper variant="outlined" sx={{ p: 1, fontFamily: 'monospace', fontSize: 13, letterSpacing: '0.1em', mb: 2, textAlign: 'center' }}>
                {secret}
              </Paper>
              <Typography gutterBottom>3. Enter the <strong>6-digit code</strong> shown in your app to confirm:</Typography>
              <TextField fullWidth label="Verification Code" value={setupCode}
                onChange={e => { setSetupCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6)); setSetupError(''); }}
                inputProps={{ inputMode: 'numeric', style: { letterSpacing: '0.4em', fontSize: 22, textAlign: 'center' } }}
                sx={{ mt: 1 }}
                autoFocus
              />
            </Box>
          )}

          {setupStep === 2 && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircle color="success" sx={{ fontSize: 72, mb: 2 }} />
              <Typography variant="h6" fontWeight={700} gutterBottom>2FA is now active!</Typography>
              <Typography color="text.secondary">
                Your account is protected. You'll be asked for a code on every sign-in.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {setupStep === 1 && (
            <>
              <Button onClick={() => setSetupOpen(false)}>Cancel</Button>
              <Button variant="contained" color="success"
                onClick={confirmSetup}
                disabled={setupLoading || setupCode.length !== 6}>
                {setupLoading ? <CircularProgress size={20} /> : 'Activate 2FA'}
              </Button>
            </>
          )}
          {setupStep === 2 && (
            <Button variant="contained" onClick={() => setSetupOpen(false)}>Done</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Disable 2FA Dialog ── */}
      <Dialog open={disableOpen} onClose={() => setDisableOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" /> Disable Two-Factor Authentication
        </DialogTitle>
        <DialogContent>
          {disableError && <Alert severity="error" sx={{ mb: 2 }}>{disableError}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>
            Disabling 2FA makes your account less secure. You'll need your current authenticator code to confirm.
          </Alert>
          <TextField fullWidth label="Current authenticator code"
            value={disableCode}
            onChange={e => { setDisableCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6)); setDisableError(''); }}
            inputProps={{ inputMode: 'numeric', style: { letterSpacing: '0.4em', fontSize: 22, textAlign: 'center' } }}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDisableOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDisable}
            disabled={disableLoading || disableCode.length !== 6}>
            {disableLoading ? <CircularProgress size={20} /> : 'Disable 2FA'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
